import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import styled from "@emotion/styled";
import { keyframes } from "@emotion/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLink, faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

import NavBar from "../components/common/navBar";
import Footer from "../components/common/footer";
import Article from "../components/articles/article";

import SEO from "../data/seo";
import { useTheme } from "../contexts/ThemeContext";
import { useLanguage } from "../contexts/LanguageContext";
import { useArticles } from "../hooks/useArticles";
import { useUserInfo } from "../hooks/useUserInfo";
import { Theme } from "../theme";

// アニメーション
const fadeInUp = keyframes`
	from {
		opacity: 0;
		transform: translateY(30px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
`;

// Styled Components
const PageContainer = styled.div<{ theme: Theme }>`
	min-height: 100vh;
	background: ${props => props.theme.colors.background.primary};
	transition: background-color 0.3s ease;
`;

const ContentWrapper = styled.div<{ theme: Theme }>`
	max-width: ${props => props.theme.density.contentMaxWidth};
	margin: 0 auto;
	padding: 0 ${props => props.theme.density.pageGutter};

	@media (max-width: 768px) {
		padding: 0 ${props => props.theme.density.pageGutterMobile};
	}
`;

const ArticlesContainer = styled.div`
	padding-top: 1.5rem;
	animation: ${fadeInUp} 0.6s ease-out;
`;

const Title = styled.h1<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.pageHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.pageHeading.fontWeight};
	color: ${props => props.theme.colors.text.primary};
	line-height: ${props => props.theme.typography.roles.pageHeading.lineHeight};
	letter-spacing: ${props => props.theme.typography.roles.pageHeading.letterSpacing};
	margin: 0 0 0.75rem;

	@media (max-width: 768px) {
		letter-spacing: -0.015em;
	}
`;

const Subtitle = styled.p<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.body.fontSize};
	line-height: ${props => props.theme.typography.roles.body.lineHeight};
	color: ${props => props.theme.colors.text.secondary};
	margin-bottom: 20px;

	@media (max-width: 768px) {
		font-size: 1.1rem;
	}
`;

const ExternalLink = styled.div<{ theme: Theme }>`
	margin: 20px 0 40px 0;

	a {
		color: ${props => props.theme.colors.primary.main};
		text-decoration: none;
		font-weight: 600;
		transition: all 0.2s ease;

		&:hover {
			color: ${props => props.theme.colors.primary.light};
		}
	}

	svg {
		margin-right: 10px;
		color: ${props => props.theme.colors.text.tertiary};
	}
`;

const ArticlesWrapper = styled.div<{ theme: Theme }>`
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
	gap: ${props => props.theme.density.gridGap};
	animation: ${fadeInUp} 0.6s ease-out 0.2s both;

	@media (max-width: 768px) {
		grid-template-columns: 1fr;
		gap: 1.5rem;
	}
`;

const Section = styled.section`
	margin-top: 3rem;
`;

const SectionTitle = styled.h2<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.sectionHeading.fontSize};
	font-weight: ${props => props.theme.typography.roles.sectionHeading.fontWeight};
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 1rem;

	@media (max-width: 768px) {
		font-size: 1.5rem;
	}
`;

const SectionDescription = styled.p<{ theme: Theme }>`
	font-size: ${props => props.theme.typography.roles.body.fontSize};
	color: ${props => props.theme.colors.text.secondary};
	margin-bottom: 2rem;
	line-height: 1.6;
`;

const TalkCard = styled.a<{ theme: Theme }>`
	display: block;
	background: ${props => props.theme.colors.background.secondary};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	border-radius: 1rem;
	padding: ${props => props.theme.density.cardPadding};
	text-decoration: none;
	transition: border-color 0.2s ease, transform 0.2s ease;
	box-shadow: ${props => props.theme.shadows.base};

	&:hover {
		transform: translateY(-2px);
		border-color: ${props => props.theme.colors.primary.main};
	}

	@media (max-width: 768px) {
		padding: ${props => props.theme.density.cardPaddingMobile};
	}
`;

const TalkHeader = styled.div`
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 1rem;
	gap: 1rem;

	@media (max-width: 768px) {
		flex-direction: column;
	}
`;

const TalkEvent = styled.span<{ theme: Theme }>`
	display: inline-block;
	background: ${props => props.theme.colors.background.tertiary};
	color: ${props => props.theme.colors.primary.main};
	border: 1px solid ${props => props.theme.colors.neutral[200]};
	padding: 0.35rem 0.65rem;
	border-radius: ${props => props.theme.borderRadius.full};
	font-family: ${props => props.theme.typography.fontFamily.mono};
	font-size: ${props => props.theme.typography.roles.caption.fontSize};
	font-weight: 600;
	white-space: nowrap;
`;

const TalkDate = styled.span<{ theme: Theme }>`
	color: ${props => props.theme.colors.text.tertiary};
	font-size: 0.875rem;
	white-space: nowrap;
`;

const TalkTitle = styled.h3<{ theme: Theme }>`
	font-size: 1.25rem;
	font-weight: 600;
	color: ${props => props.theme.colors.text.primary};
	margin-bottom: 0.75rem;
	line-height: 1.4;

	@media (max-width: 768px) {
		font-size: 1.125rem;
	}
`;

const TalkDescription = styled.p<{ theme: Theme }>`
	font-size: 1rem;
	color: ${props => props.theme.colors.text.secondary};
	line-height: 1.6;
	margin-bottom: 1rem;
`;

const TalkLink = styled.span<{ theme: Theme }>`
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	color: ${props => props.theme.colors.primary.main};
	font-weight: 600;
	font-size: 0.95rem;

	svg {
		transition: transform 0.2s ease;
	}

	${TalkCard}:hover & svg {
		transform: translateX(2px);
	}
`;

const TalksWrapper = styled.div`
	display: flex;
	flex-direction: column;
	gap: 1.5rem;
`;

const FooterWrapper = styled.div`
	margin-top: 3rem;
`;

const Articles = () => {
	const { theme } = useTheme();
	const { t } = useLanguage();
	const articles = useArticles();
	const userInfo = useUserInfo();

	useEffect(() => {
		window.scrollTo(0, 0);
	}, []);

	const currentSEO = SEO.find((item) => item.page === "articles")!;

	return (
		<React.Fragment>
			<Helmet>
				<title>{`Articles | ${userInfo.main.title}`}</title>
				<meta name="description" content={currentSEO.description} />
				<meta name="keywords" content={currentSEO.keywords.join(", ")} />
			</Helmet>

			<PageContainer theme={theme}>
				<NavBar active="articles" />
				<ContentWrapper theme={theme}>
					<ArticlesContainer>
						<Title theme={theme}>{userInfo.outputs.title}</Title>
						<Subtitle theme={theme}>{userInfo.outputs.description}</Subtitle>

						<ExternalLink theme={theme}>
							<FontAwesomeIcon icon={faLink} />
							<a
								href="https://qiita.com/s2mr"
								target="_blank"
								rel="noopener noreferrer"
							>
								Qiita
							</a>
						</ExternalLink>

					<ArticlesWrapper theme={theme}>
							{articles.map((article, index) => (
								<Article
									key={(index + 1).toString()}
									date={article.date}
									title={article.title}
									description={article.description}
									link={article.link}
								/>
							))}
						</ArticlesWrapper>

						{userInfo.talks && userInfo.talks.items.length > 0 && (
							<Section>
								<SectionTitle theme={theme}>{userInfo.talks.title}</SectionTitle>
								<SectionDescription theme={theme}>
									{userInfo.talks.description}
								</SectionDescription>
								<TalksWrapper>
									{userInfo.talks.items.map((talk, index) => (
										<TalkCard
											key={index}
											href={talk.link}
											target="_blank"
											rel="noopener noreferrer"
											theme={theme}
										>
											<TalkHeader>
												<TalkEvent theme={theme}>{talk.event}</TalkEvent>
												<TalkDate theme={theme}>{talk.date}</TalkDate>
											</TalkHeader>
											<TalkTitle theme={theme}>{talk.title}</TalkTitle>
											{talk.description && (
												<TalkDescription theme={theme}>
													{talk.description}
												</TalkDescription>
											)}
											<TalkLink theme={theme}>
												{t.talks.viewTalk}
												<FontAwesomeIcon icon={faExternalLinkAlt} size="sm" />
											</TalkLink>
										</TalkCard>
									))}
								</TalksWrapper>
							</Section>
						)}
					</ArticlesContainer>
					<FooterWrapper>
						<Footer />
					</FooterWrapper>
				</ContentWrapper>
			</PageContainer>
		</React.Fragment>
	);
};

export default Articles;
